import { useEffect, useId, useRef, type ReactNode } from 'react'
import { FOCUSABLE, trapStep } from '../lib/focusTrap'
import { Icon } from './icons'
import { useStaffText } from './StaffText'

/**
 * A centred dialog. `tone="dark"` (charcoal header, thin brand line) is for
 * editors and the counter wizard; the light default is for short confirmations
 * and counter action dialogs. Escape and the scrim close it, Tab stays inside,
 * focus returns on close. Admin editors use admin/Sheet, which wears the dark look.
 */
export function Dialog({
  title,
  sub,
  tone = 'light',
  wide = false,
  onClose,
  footer,
  footNote,
  children,
}: {
  title: string
  /** One short line under the title. */
  sub?: string
  tone?: 'light' | 'dark'
  wide?: boolean
  onClose: () => void
  /** Buttons, right-aligned: Cancel then the primary action. */
  footer?: ReactNode
  /** A few quiet words at the footer's left. */
  footNote?: ReactNode
  children: ReactNode
}) {
  const { say } = useStaffText()
  const box = useRef<HTMLDivElement>(null)
  const titleId = useId()
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    const before = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const field = box.current?.querySelector<HTMLElement>('.dialog__body input, .dialog__body select, .dialog__body textarea')
    ;(field ?? box.current)?.focus()
    const on = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) closeRef.current()
      if (e.key === 'Tab' && !e.defaultPrevented && box.current) {
        const items = [...box.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null)
        const active = document.activeElement instanceof HTMLElement ? document.activeElement : null
        const to = trapStep(items, active, e.shiftKey)
        if (to) {
          e.preventDefault()
          to.focus()
        }
      }
    }
    addEventListener('keydown', on)
    return () => {
      removeEventListener('keydown', on)
      before?.focus()
    }
  }, [])

  return (
    <div className="dialog-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={box}
        className={`dialog${tone === 'dark' ? ' dialog--dark' : ''}${wide ? ' dialog--wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header className="dialog__head">
          <div className="dialog__titles">
            <h2 id={titleId} className="dialog__title">
              {title}
            </h2>
            {sub && <p className="dialog__sub">{sub}</p>}
          </div>
          <button type="button" className="dialog__close" onClick={onClose} aria-label={say('ui.close')}>
            <Icon name="close" size={16} />
          </button>
        </header>
        <div className="dialog__body">{children}</div>
        {(footer || footNote) && (
          <footer className="dialog__foot">
            {footNote && <span className="dialog__foot-note">{footNote}</span>}
            {footer}
          </footer>
        )}
      </div>
    </div>
  )
}
